import { useCallback } from 'react';
// The key list comes from the dashboard's own mirror rather than from `@hbs-us/contracts`.
//
// Both exist, and they must agree. The mirror is the one that moves first: a key added to the
// backend is usable here as soon as it is mirrored, whereas importing the published package means
// waiting for a changeset release before a gate can even be typed. That wait is what this import
// used to impose — quality control's four keys existed in the worker and in the permissions screen
// while every `useAccess({ permission: 'qc.view' })` was still a type error.
//
// The mirror is not a second source of truth for BEHAVIOUR; the api worker still decides every
// grant. It is a source of truth for what the client is allowed to ask about.
import type { PermissionKey } from '../features/users/contractsMirror.js';
import type { SessionUser } from './authClient.js';
import { useSession } from './useSession.js';

/**
 * The single client-side access primitive. Before this existed every gate was
 * hand-written, so the dashboard shipped with exactly TWO checks in the whole app
 * and none on the sidebar — users saw every destination and learned they lacked
 * access from a 403.
 *
 * HIDING IS UX, NOT AUTHORIZATION. The api worker enforces the same rules again on
 * every request and is the only thing standing between a user and the data. Never
 * remove a server check because the button is hidden here.
 *
 * The api worker guards routes two different ways, so this mirrors both or the two
 * sides drift:
 *   - `requireApiPermission(c, user, 'jobs.view')` — a key from PERMISSION_KEYS.
 *   - `requireApiAdmin` / `requireManagerOrAdmin` — the caller's ROLE, ignoring keys.
 * A key-only model would show `/payroll` to any user carrying an override that
 * happens to look financial, and the server would still 403 them on role.
 */

export type AccessRule =
  /** Reachable by every signed-in user. Deliberate, and must stay explicit — the
   *  lint:nav-permissions gate accepts this but rejects a missing rule. */
  'always' | { permission: PermissionKey } | { minRole: 'Manager' | 'Admin' };

/** Mirrors the api worker's role ladder. Unknown roles rank below Employee. */
const ROLE_RANK: Record<string, number> = { Employee: 0, Manager: 1, Admin: 2 };

/**
 * Pure resolver — no React, so tests and the lint gate can call it directly.
 * An Admin satisfies permission rules through their resolved `permissions` array
 * (the server's Admin preset carries every key), not through a special case here.
 */
export function grantsAccess(user: SessionUser | null | undefined, rule: AccessRule): boolean {
  if (rule === 'always') return true;
  if (!user) return false;
  if ('minRole' in rule) {
    return (ROLE_RANK[user.role] ?? -1) >= ROLE_RANK[rule.minRole];
  }
  return user.permissions.includes(rule.permission);
}

/**
 * Returns a stable predicate for the current session. Use it to decide whether to
 * RENDER something; never to decide whether an action is safe.
 */
export function useAccess(): (rule: AccessRule) => boolean {
  const { session } = useSession();
  const user = session?.user ?? null;
  return useCallback((rule: AccessRule) => grantsAccess(user, rule), [user]);
}
